<template>
  <div class="view-inspector" :class="{ 'view-inspector_vip': vip }">
    <el-divider class="view-inspector__divider" />
    <div class="view-inspector__top">
      <div class="view-inspector__top-item">
        <div class="view-inspector__value">
          <div class="view-inspector__value-refresh">
            {{
              field?.latest_subscription?.refresh_date
                ? formatDate(field?.latest_subscription?.refresh_date)
                : "-"
            }}
          </div>
          <el-tag
            class="users-page__first-l-upper"
            :type="getTypeByStatus(field?.status)"
            >{{ field?.status || "None" }}</el-tag
          >
          <div class="view-inspector__platform">
            {{ field?.latest_subscription?.platform }}
          </div>
        </div>
        <div class="view-inspector__prop">Refresh Date</div>
      </div>
      <div class="view-inspector__top-item">
        <div class="view-inspector__value">
          <div class="view-inspector__value-expiration">
            {{
              vip
                ? "Never"
                : field?.latest_subscription?.expire_date
                ? formatDate(field?.latest_subscription?.expire_date)
                : "-"
            }}
          </div>
        </div>
        <div class="view-inspector__prop">Expiration Date</div>
      </div>
      <div class="view-inspector__top-items">
        <div class="view-inspector__top-item">
          <div class="view-inspector__value">
            <div class="view-inspector__value-subscription">
              {{ vip ? "No" : field.subscription_type || "-" }}
              {{ is_in_trial_period ? `(Trial)` : "" }}
            </div>
          </div>
          <div class="view-inspector__prop">Subscription Type</div>
        </div>
        <div class="view-inspector__top-item">
          <div class="view-inspector__value">
            <el-switch
              size="small"
              v-model="vipStatus"
              @change="toggleVipStatus"
              aria-label="VIP Status"
            />
          </div>
          <div class="view-inspector__prop">VIP Status</div>
        </div>
      </div>
    </div>
    <div class="view-inspector__actions">
      <el-button
        v-if="subscriptions?.length"
        size="small"
        type="primary"
        @click="openHistory"
      >
        History
      </el-button>
      <el-button size="small" type="success" @click="handleRefresh">
        Refresh
      </el-button>
    </div>
    <el-divider />
    <el-tabs v-model="activeName" class="demo-tabs">
      <el-tab-pane label="Main" name="main"
        ><CardDevice
          v-for="device in user.devices"
          :key="device.id"
          :version="device.app_version"
          :date="device.last_active_at"
          :platform="device.platform"
        />
        <template v-if="user.offers && user.offers.length">
          <el-divider />
          <CardOffer
            class="view-inspector__offer"
            v-for="offer in user.offers"
            :key="offer.id"
            :title="offer.offer.name"
            :date="offer.sent_date"
            :status="offer.offer.status"
          />
        </template>
      </el-tab-pane>
      <el-tab-pane label="Notes" name="notes">
        <div class="view-inspector__notes">
          <UserNote
            class="view-inspector__note"
            v-for="comment in user.comments"
            :key="comment.id"
            :title="comment.creator_name"
            :comment="comment.comment"
            :date="comment.updated_at"
          />
          <el-form
            ref="ruleFormRef"
            :model="ruleForm"
            :rules="rules"
            label-position="top"
            class="view-inspector__form"
            size="small"
          >
            <div class="view-inspector__form-title">You</div>
            <el-form-item label="" prop="comment">
              <el-input
                v-model="ruleForm.comment"
                :autosize="{ minRows: 10 }"
                type="textarea"
                class="view-inspector__textarea"
              />
            </el-form-item>
            <el-button
              @click="onSubmit(ruleFormRef)"
              type="primary"
              size="default"
              style="width: 100%"
              >Add Comment</el-button
            >
          </el-form>
        </div>
      </el-tab-pane>
    </el-tabs>
    <el-dialog v-model="isHistoryModalOpen" title="History" width="700">
      <el-table
        :data="subscriptions"
        height="100%"
        style="width: 100%; border-radius: 4px"
      >
        <el-table-column prop="expire_date" label="Expiration Date" width="130">
          <template #default="{ row }">
            {{ formatDate(row.expire_date) }}
          </template>
        </el-table-column>
        <el-table-column
          prop="is_in_trial_period"
          label="In Trial Period"
          width="130"
        />
        <el-table-column prop="platform" label="Platform" width="130" />
        <el-table-column prop="product_sku" label="Product" width="300" />
        <el-table-column prop="start_date" label="Start Date" width="130">
          <template #default="{ row }">
            {{ formatDate(row.expire_date) }}
          </template>
        </el-table-column>
        <el-table-column
          prop="subscription_type"
          label="Subscription Type"
          width="150"
        />
        <el-table-column prop="uuid" label="UUID" width="300" />
      </el-table>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, defineProps, reactive, defineEmits, watch } from "vue";
import { useStore } from "vuex";
import { CardDevice } from "@/shared/ui/components/CardDevice";
import { CardOffer } from "@/shared/ui/components/CardOffer";
import { UserNote } from "@/shared/ui/components/UserNote";

const store = useStore();

const props = defineProps({
  field: {
    type: Object,
    default: () => ({}),
  },
  vip: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["on-refresh", "on-toggle-vip"]);

const ruleFormRef = ref();
const isHistoryModalOpen = ref(false);
const vipStatus = ref(props.vip);

watch(
  () => props.vip,
  (newVal) => {
    vipStatus.value = newVal; // Keep internal state in sync with prop
  }
);

const ruleForm = reactive({
  comment: "",
});

const rules = reactive({
  comment: [{ required: true, trigger: "blur", message: "" }],
});

const activeName = ref("main");

const user = computed(() => {
  return store.state.users.user;
});

const subscriptions = computed(() => {
  return user.value.subscriptions;
});

const handleRefresh = () => {
  emit("on-refresh");
};

const toggleVipStatus = (newStatus) => {
  console.log(newStatus);
  emit("on-toggle-vip", newStatus);
};

const openHistory = () => {
  isHistoryModalOpen.value = true;
};

const onSubmit = async (formEl) => {
  if (!formEl) return;
  formEl.validate((valid) => {
    if (valid) {
      ruleForm.user_uuid = props.field.uuid;
      store.dispatch("users/createComment", ruleForm);
      ruleForm.comment = "";
    } else {
      return false;
    }
  });
};

const getTypeByStatus = (status) => {
  switch (status) {
    case "vip":
      return "primary";
    case "expired":
      return "danger";
    case "active":
      return "success";
    case "none":
      return "info";
    default:
      return "info";
  }
};

const formatDate = (date) => {
  const dateNew = new Date(date);

  const options = {
    year: "numeric",
    month: "short",
    day: "numeric",
  };

  const formattedDate = dateNew.toLocaleString("en-US", options);
  return formattedDate;
};
</script>

<style lang="scss">
.view-inspector {
  overflow: auto;
  height: 100%;
  -ms-overflow-style: none;
  scrollbar-width: none;
  padding-bottom: 62px;
  &::-webkit-scrollbar {
    display: none;
  }
  &_vip {
    .view-inspector__value-refresh {
      display: none;
    }
    .view-inspector__value {
      color: var(--Color-Text-text-color-secondary, #909399);
    }
  }
  &__top {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  &__top-items {
    display: flex;
    align-items: center;
    justify-content: space-between;
    .view-inspector__top-item {
      flex-grow: 1;
    }
  }
  &__prop {
    color: var(--Color-Text-text-color-secondary, #909399);
    font-size: 12px;
    font-weight: 500;
    line-height: 20px;
  }
  &__value {
    display: flex;
    align-items: start;
    gap: 8px;
    color: var(--Color-Text-text-color-primary, #303133);
  }
  &__value-refresh {
    font-size: 20px;
    font-weight: 800;
    line-height: 28px;
  }
  &__value-expiration {
    font-size: 16px;
    font-weight: 800;
    line-height: 24px;
  }
  &__value-subscription {
    font-size: 13px;
    font-weight: 800;
    line-height: 22px;
    &::first-letter {
      text-transform: uppercase;
    }
  }
  &__offer {
    margin-bottom: 10px;
    &:last-child {
      margin-bottom: 0;
    }
  }
  &__note {
    margin-bottom: 24px;
  }
  &__form-title {
    color: var(--Color-Text-text-color-primary, #303133);
    font-size: 16px;
    font-weight: 800;
    line-height: 24px;
    margin-bottom: 4px;
  }
  &__textarea {
    margin-bottom: 8px;
  }
  &__actions {
    margin-top: 10px;
  }
  &__platform {
    font-size: 14px;
    font-weight: 500;
    line-height: 24px;
  }
}
</style>
